import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYesterday,
  getDay,
  getWeek,
  setDay,
  setWeek,
  startOfMonth,
  startOfWeek,
  sub,
} from 'date-fns'
import type { Timeframe } from '../components/TimeframeSelect'

export type DatesForTimeframe = {
  start: Date
  end: Date
  startPrevious: Date
  endPrevious: Date
  previousPeriods: (readonly [Date, Date])[]
  startSelectedPeriodOfPreviousYear: Date
  endSelectedPeriodOfPreviousYear: Date
  timeframe: Timeframe
}

const getSameWeekPreviousYear = (start: Date, end: Date) => {
  const startSameWeekPreviousYear = startOfWeek(
    setWeek(sub(start, { years: 1 }), getWeek(start)),
    { weekStartsOn: 1 },
  )
  const endSameWeekPreviousYear = endOfDay(
    setDay(startSameWeekPreviousYear, getDay(end)),
  )

  return { startSameWeekPreviousYear, endSameWeekPreviousYear }
}

const getPrevious8Weeks = (start: Date, end: Date) => {
  const previousPeriods = Array.from(
    { length: 8 },
    (_, i) =>
      [sub(start, { weeks: i + 1 }), sub(end, { weeks: i + 1 })] as const,
  ).toReversed()

  return previousPeriods
}

const getPrevious3Months = (start: Date, end: Date) => {
  const previousPeriods = Array.from(
    { length: 3 },
    (_, i) =>
      [sub(start, { months: i + 1 }), sub(end, { months: i + 1 })] as const,
  ).toReversed()

  return previousPeriods
}

const getDatesForThisWeek = (): DatesForTimeframe => {
  const end = endOfYesterday()
  const start = startOfWeek(end, { weekStartsOn: 1 })

  const startPrevious = sub(start, { weeks: 1 })
  const endPrevious = sub(end, { weeks: 1 })

  const previous8Weeks = getPrevious8Weeks(start, end)

  const { startSameWeekPreviousYear, endSameWeekPreviousYear } =
    getSameWeekPreviousYear(start, end)

  return {
    start,
    end,
    startPrevious,
    endPrevious,
    previousPeriods: previous8Weeks,
    startSelectedPeriodOfPreviousYear: startSameWeekPreviousYear,
    endSelectedPeriodOfPreviousYear: endSameWeekPreviousYear,
    timeframe: 'this-week',
  }
}

const getDatesForLastWeek = (): DatesForTimeframe => {
  const oneWeekAgo = sub(new Date(), { weeks: 1 })
  const start = startOfWeek(oneWeekAgo, { weekStartsOn: 1 })
  const end = endOfWeek(oneWeekAgo, { weekStartsOn: 1 })

  const startPrevious = sub(start, { weeks: 1 })
  const endPrevious = sub(end, { weeks: 1 })

  const previous8Weeks = getPrevious8Weeks(start, end)

  const { startSameWeekPreviousYear, endSameWeekPreviousYear } =
    getSameWeekPreviousYear(start, end)

  return {
    start,
    end,
    startPrevious,
    endPrevious,
    previousPeriods: previous8Weeks,
    startSelectedPeriodOfPreviousYear: startSameWeekPreviousYear,
    endSelectedPeriodOfPreviousYear: endSameWeekPreviousYear,
    timeframe: 'last-week',
  }
}

const getDatesForThisMonth = (): DatesForTimeframe => {
  const end = endOfYesterday()
  const start = startOfMonth(end)

  const startPrevious = sub(start, { months: 1 })
  const endPrevious = sub(end, { months: 1 })

  const previous3Months = getPrevious3Months(start, end)

  const startSelectedPeriodOfPreviousYear = sub(start, { years: 1 })
  const endSelectedPeriodOfPreviousYear = sub(end, { years: 1 })

  return {
    start,
    end,
    startPrevious,
    endPrevious,
    previousPeriods: previous3Months,
    startSelectedPeriodOfPreviousYear,
    endSelectedPeriodOfPreviousYear,
    timeframe: 'this-month',
  }
}

const getDatesForLastMonth = (): DatesForTimeframe => {
  const now = new Date()
  now.setMonth(now.getMonth() - 1)
  const start = startOfMonth(now)
  const end = endOfMonth(now)

  const startPrevious = sub(start, { months: 1 })
  const endPrevious = endOfMonth(sub(end, { months: 1 }))

  const previous3Months = getPrevious3Months(start, end)

  const startSelectedPeriodOfPreviousYear = sub(start, { years: 1 })
  const endSelectedPeriodOfPreviousYear = endOfMonth(sub(end, { years: 1 }))

  return {
    start,
    end,
    startPrevious,
    endPrevious,
    previousPeriods: previous3Months,
    startSelectedPeriodOfPreviousYear,
    endSelectedPeriodOfPreviousYear,
    timeframe: 'last-month',
  }
}

export const getDatesForTimeframe = (timeframe: Timeframe) => {
  const dateLookup = {
    'last-week': getDatesForLastWeek(),
    'this-week': getDatesForThisWeek(),
    'last-month': getDatesForLastMonth(),
    'this-month': getDatesForThisMonth(),
  }
  return dateLookup[timeframe]
}
